import { useApiClient } from '@skylabs-digital/react-proto-kit';
import { useMemo } from 'react';
import { fileSchema } from '../schemas/file.schema';
import {
  type ProcessingOverview,
  type ProcessingSeries,
  type ProcessingWindow,
  processingOverviewSchema,
  processingSeriesSchema,
} from '../schemas/processing.schema';
import type { FilesPage } from './files.api';
import { unwrap } from './unwrap';

export interface ProcessingQuery {
  window: ProcessingWindow;
  appId?: string;
}

/** Sólo lo que el endpoint declara: un appId vacío no viaja. */
export function buildProcessingQuery(q: ProcessingQuery): string {
  const params = new URLSearchParams({ window: q.window });
  if (q.appId) params.set('appId', q.appId);
  return params.toString();
}

export function useProcessingActions() {
  const { connector } = useApiClient();
  // Memoizado sobre el connector: `ProcessingPage` cuelga su polling de estos métodos.
  return useMemo(
    () => ({
      overview: async (q: ProcessingQuery): Promise<ProcessingOverview> =>
        processingOverviewSchema.parse(
          unwrap(await connector.get<unknown>(`processing/overview?${buildProcessingQuery(q)}`)),
        ),
      series: async (q: ProcessingQuery): Promise<ProcessingSeries> =>
        processingSeriesSchema.parse(
          unwrap(await connector.get<unknown>(`processing/series?${buildProcessingQuery(q)}`)),
        ),
      failures: async (q: { appId?: string; page: number; limit: number }): Promise<FilesPage> => {
        const params = new URLSearchParams({ page: String(q.page), limit: String(q.limit) });
        if (q.appId) params.set('appId', q.appId);
        const res = await connector.get<unknown[]>(`processing/failures?${params}`);
        const data = unwrap(res).map((f) => fileSchema.parse(f));
        const meta = (res as { meta?: FilesPage['meta'] }).meta ?? {
          total: data.length,
          page: q.page,
          limit: q.limit,
          totalPages: 1,
          hasNext: false,
          hasPrev: q.page > 1,
        };
        return { data, meta };
      },
      retry: async (appId: string, fileId: string): Promise<void> => {
        unwrap(await connector.post<unknown>(`apps/${appId}/files/${fileId}/processing/retry`, {}));
      },
    }),
    [connector],
  );
}
